import React, { useState, useEffect, useContext } from 'react';
import { AppContext } from '../../App';
import SuperAdminLayout from '../../components/SuperAdminLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import { 
  Mail, 
  Users, 
  Clock, 
  Send, 
  Edit, 
  Info, 
  CheckCircle,
  MessageSquare,
  Settings
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SuperAdminDripCampaigns() {
  const { authAxios } = useContext(AppContext);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [activeTab, setActiveTab] = useState('email');
  const [formData, setFormData] = useState({
    is_enabled: false,
    email_enabled: true,
    template_id_email_1: '',
    template_id_email_2: '',
    sms_enabled: false,
    sms_message_1: '',
    sms_message_2: '',
    days_before_email_1: 3,
    days_between_emails: 3
  });
  const [campaignStats, setCampaignStats] = useState({});

  useEffect(() => {
    loadCampaigns();
  }, []);

  const loadCampaigns = async () => {
    try {
      const response = await authAxios.get(`${API}/platform/drip-campaigns`);
      setCampaigns(response.data);
      
      // Load stats for each campaign
      const stats = {};
      for (const campaign of response.data) {
        try {
          const statsResponse = await authAxios.get(`${API}/platform/drip-campaigns/${campaign.id}/stats`);
          stats[campaign.id] = statsResponse.data;
        } catch (e) {
          console.error('Failed to load stats for', campaign.id);
        }
      }
      setCampaignStats(stats);
    } catch (error) {
      console.error('Failed to load drip campaigns:', error);
      toast.error('Failed to load drip campaigns');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (campaign) => {
    setEditingCampaign(campaign);
    setFormData({
      is_enabled: campaign.is_enabled,
      email_enabled: campaign.email_enabled ?? true,
      template_id_email_1: campaign.template_id_email_1 || '',
      template_id_email_2: campaign.template_id_email_2 || '',
      sms_enabled: campaign.sms_enabled ?? false,
      sms_message_1: campaign.sms_message_1 || '',
      sms_message_2: campaign.sms_message_2 || '',
      days_before_email_1: campaign.days_before_email_1 || 3,
      days_between_emails: campaign.days_between_emails || 3
    });
    setActiveTab('email');
  };

  const handleSave = async () => {
    try {
      await authAxios.patch(`${API}/platform/drip-campaigns/${editingCampaign.id}`, formData);
      toast.success('Campaign updated successfully');
      setEditingCampaign(null);
      loadCampaigns();
    } catch (error) {
      console.error('Failed to update drip campaign:', error);
      toast.error('Failed to update drip campaign');
    }
  };

  const handleToggleEnabled = async (campaign) => {
    try {
      await authAxios.patch(`${API}/platform/drip-campaigns/${campaign.id}`, {
        is_enabled: !campaign.is_enabled
      });
      toast.success(campaign.is_enabled ? 'Campaign disabled' : 'Campaign enabled');
      loadCampaigns();
    } catch (error) {
      console.error('Failed to toggle campaign:', error);
      toast.error('Failed to update campaign');
    }
  };

  const getCampaignIcon = (id) => {
    switch (id) {
      case 'drip_no_league': return <Users className="w-5 h-5" />;
      case 'drip_no_season': return <Clock className="w-5 h-5" />;
      case 'drip_no_invites': return <Send className="w-5 h-5" />;
      default: return <Mail className="w-5 h-5" />;
    }
  };

  const getChannelBadges = (campaign) => {
    const badges = [];
    if (campaign.email_enabled && (campaign.template_id_email_1 || campaign.template_id_email_2)) {
      badges.push(
        <Badge key="email" variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200">
          <Mail className="w-3 h-3 mr-1" /> Email
        </Badge>
      );
    }
    if (campaign.sms_enabled && (campaign.sms_message_1 || campaign.sms_message_2)) {
      badges.push(
        <Badge key="sms" variant="outline" className="text-xs bg-emerald-50 text-emerald-700 border-emerald-200">
          <MessageSquare className="w-3 h-3 mr-1" /> SMS
        </Badge>
      );
    }
    return badges;
  };

  if (loading) {
    return (
      <SuperAdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600"></div>
        </div>
      </SuperAdminLayout>
    );
  }

  return (
    <SuperAdminLayout>
      <div className="max-w-5xl mx-auto">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-zinc-950">Drip Campaigns</h1>
          <p className="text-zinc-700 mt-1">
            Automated email & SMS sequences to help users complete their onboarding journey
          </p>
        </div>

        {/* Info Card */}
        <Card className="mb-6 bg-blue-50 border-blue-200">
          <CardContent className="pt-4">
            <div className="flex items-start gap-3">
              <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-blue-800">
                <p className="font-medium mb-1">How Drip Campaigns Work</p>
                <ul className="list-disc list-inside space-y-1 text-blue-700">
                  <li>Each campaign targets users in a specific "stuck" state</li>
                  <li>Two messages are sent per campaign (Email and/or SMS) with configurable timing</li>
                  <li>Messages stop automatically when the user completes the required action</li>
                  <li>Campaigns run automatically every 6 hours</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Campaign Cards */}
        <div className="space-y-4">
          {campaigns.map((campaign) => {
            const stats = campaignStats[campaign.id];
            const hasEmailConfig = campaign.template_id_email_1 || campaign.template_id_email_2;
            const hasSmsConfig = campaign.sms_message_1 || campaign.sms_message_2;
            const hasAnyConfig = hasEmailConfig || hasSmsConfig;
            
            return (
              <Card key={campaign.id} className={`${campaign.is_enabled ? 'border-emerald-200 bg-emerald-50/30' : ''}`}>
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-lg ${campaign.is_enabled ? 'bg-emerald-100 text-emerald-600' : 'bg-slate-100 text-zinc-500'}`}>
                        {getCampaignIcon(campaign.id)}
                      </div>
                      <div>
                        <CardTitle className="text-lg flex items-center gap-2">
                          {campaign.name}
                          {campaign.is_enabled ? (
                            <Badge className="bg-emerald-100 text-emerald-700 text-xs">Active</Badge>
                          ) : (
                            <Badge variant="secondary" className="text-xs">Inactive</Badge>
                          )}
                        </CardTitle>
                        <CardDescription className="mt-0.5">{campaign.description}</CardDescription>
                        <div className="flex gap-2 mt-2">
                          {getChannelBadges(campaign)}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={campaign.is_enabled}
                        onCheckedChange={() => handleToggleEnabled(campaign)}
                        disabled={!hasAnyConfig}
                        data-testid={`toggle-${campaign.id}`}
                      />
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleEdit(campaign)}
                        data-testid={`edit-${campaign.id}`}
                      >
                        <Edit className="w-4 h-4 mr-1" />
                        Configure
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                    <div className="bg-white rounded-lg p-3 border">
                      <div className="text-xs text-zinc-500 uppercase tracking-wide">Message 1 After</div>
                      <div className="text-lg font-semibold text-zinc-950">{campaign.days_before_email_1} days</div>
                    </div>
                    <div className="bg-white rounded-lg p-3 border">
                      <div className="text-xs text-zinc-500 uppercase tracking-wide">Message 2 After</div>
                      <div className="text-lg font-semibold text-zinc-950">+{campaign.days_between_emails} days</div>
                    </div>
                    <div className="bg-white rounded-lg p-3 border">
                      <div className="text-xs text-zinc-500 uppercase tracking-wide">Email Templates</div>
                      <div className="text-sm font-mono text-zinc-800">
                        {hasEmailConfig ? (
                          <span className="text-emerald-600">Configured</span>
                        ) : (
                          <span className="text-zinc-400">Not set</span>
                        )}
                      </div>
                    </div>
                    <div className="bg-white rounded-lg p-3 border">
                      <div className="text-xs text-zinc-500 uppercase tracking-wide">SMS Messages</div>
                      <div className="text-sm text-zinc-800">
                        {hasSmsConfig ? (
                          <span className="text-emerald-600">Configured</span>
                        ) : (
                          <span className="text-zinc-400">Not set</span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Stats */}
                  {stats && (
                    <div className="flex flex-wrap items-center gap-4 text-sm text-zinc-700 border-t pt-3" data-testid={`stats-${campaign.id}`}>
                      <div className="flex items-center gap-1" data-testid={`active-users-${campaign.id}`}>
                        <Users className="w-4 h-4" />
                        <span>{stats.active_users} active</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Mail className="w-4 h-4 text-blue-500" />
                        <span>{stats.email_1_sent}/{stats.email_2_sent} emails</span>
                      </div>
                      {(stats.sms_1_sent > 0 || stats.sms_2_sent > 0) && (
                        <div className="flex items-center gap-1">
                          <MessageSquare className="w-4 h-4 text-emerald-500" />
                          <span>{stats.sms_1_sent}/{stats.sms_2_sent} SMS</span>
                        </div>
                      )}
                      <div className="flex items-center gap-1">
                        <CheckCircle className="w-4 h-4 text-emerald-500" />
                        <span>{stats.completed} completed</span>
                      </div>
                    </div>
                  )}

                  {/* Template Variables */}
                  {campaign.variables && campaign.variables.length > 0 && (
                    <div className="mt-3 pt-3 border-t">
                      <div className="text-xs font-medium text-zinc-500 mb-2">Available Template Variables:</div>
                      <div className="flex flex-wrap gap-2">
                        {campaign.variables.map((variable) => (
                          <code
                            key={variable.name}
                            className="px-2 py-1 bg-slate-100 rounded text-xs text-zinc-800"
                            title={variable.description}
                          >
                            {`{{${variable.name}}}`}
                          </code>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Edit Dialog with Tabs */}
        <Dialog open={!!editingCampaign} onOpenChange={(open) => !open && setEditingCampaign(null)}>
          <DialogContent className="max-w-lg" data-testid="edit-campaign-dialog">
            <DialogHeader>
              <DialogTitle>Configure: {editingCampaign?.name}</DialogTitle>
            </DialogHeader>
            
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="email" className="flex items-center gap-1" data-testid="tab-email">
                  <Mail className="w-4 h-4" /> Email
                </TabsTrigger>
                <TabsTrigger value="sms" className="flex items-center gap-1" data-testid="tab-sms">
                  <MessageSquare className="w-4 h-4" /> SMS
                </TabsTrigger>
                <TabsTrigger value="settings" className="flex items-center gap-1" data-testid="tab-settings">
                  <Settings className="w-4 h-4" /> Settings
                </TabsTrigger>
              </TabsList>

              {/* Email Tab */}
              <TabsContent value="email" className="space-y-4 mt-4" data-testid="tab-content-email">
                <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <div>
                    <Label className="font-medium">Email Notifications</Label>
                    <p className="text-xs text-zinc-500">Send email reminders via SendGrid</p>
                  </div>
                  <Switch
                    checked={formData.email_enabled}
                    onCheckedChange={(checked) => setFormData({ ...formData, email_enabled: checked })}
                    data-testid="switch-email-enabled"
                  />
                </div>

                {formData.email_enabled && (
                  <>
                    <div>
                      <Label>Email 1 Template ID</Label>
                      <Input
                        placeholder="d-xxxxxxxxxxxxxxxxxxxxxxxx"
                        value={formData.template_id_email_1}
                        onChange={(e) => setFormData({ ...formData, template_id_email_1: e.target.value })}
                        className="mt-1 font-mono text-sm"
                        data-testid="input-email-template-1"
                      />
                      <p className="text-xs text-zinc-500 mt-1">SendGrid Dynamic Template ID for the first email</p>
                    </div>

                    <div>
                      <Label>Email 2 Template ID</Label>
                      <Input
                        placeholder="d-xxxxxxxxxxxxxxxxxxxxxxxx"
                        value={formData.template_id_email_2}
                        onChange={(e) => setFormData({ ...formData, template_id_email_2: e.target.value })}
                        className="mt-1 font-mono text-sm"
                        data-testid="input-email-template-2"
                      />
                      <p className="text-xs text-zinc-500 mt-1">SendGrid Dynamic Template ID for the follow-up email</p>
                    </div>
                  </>
                )}
              </TabsContent>

              {/* SMS Tab */}
              <TabsContent value="sms" className="space-y-4 mt-4" data-testid="tab-content-sms">
                <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <div>
                    <Label className="font-medium">SMS Notifications</Label>
                    <p className="text-xs text-zinc-500">Send SMS reminders via Twilio</p>
                  </div>
                  <Switch
                    checked={formData.sms_enabled}
                    onCheckedChange={(checked) => setFormData({ ...formData, sms_enabled: checked })}
                    data-testid="switch-sms-enabled"
                  />
                </div>

                {formData.sms_enabled && (
                  <>
                    <div>
                      <Label>SMS Message 1</Label>
                      <Textarea
                        placeholder="Hey {{user_name}}! Don't forget to..."
                        value={formData.sms_message_1}
                        onChange={(e) => setFormData({ ...formData, sms_message_1: e.target.value })}
                        className="mt-1 text-sm"
                        rows={3}
                        data-testid="textarea-sms-message-1"
                      />
                      <div className="flex justify-between mt-1">
                        <p className="text-xs text-zinc-500">First SMS reminder</p>
                        <p className="text-xs text-zinc-500">{formData.sms_message_1.length}/160 chars</p>
                      </div>
                    </div>

                    <div>
                      <Label>SMS Message 2</Label>
                      <Textarea
                        placeholder="Hey {{user_name}}! Just a friendly reminder..."
                        value={formData.sms_message_2}
                        onChange={(e) => setFormData({ ...formData, sms_message_2: e.target.value })}
                        className="mt-1 text-sm"
                        rows={3}
                        data-testid="textarea-sms-message-2"
                      />
                      <div className="flex justify-between mt-1">
                        <p className="text-xs text-zinc-500">Follow-up SMS reminder</p>
                        <p className="text-xs text-zinc-500">{formData.sms_message_2.length}/160 chars</p>
                      </div>
                    </div>

                    {/* Variables info for SMS */}
                    {editingCampaign?.variables && editingCampaign.variables.length > 0 && (
                      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                        <div className="text-xs font-medium text-amber-800 mb-2">Available Variables:</div>
                        <div className="flex flex-wrap gap-2">
                          {editingCampaign.variables.map((variable) => (
                            <code
                              key={variable.name}
                              className="px-2 py-1 bg-white rounded text-xs text-amber-700"
                              title={variable.description}
                            >
                              {`{{${variable.name}}}`}
                            </code>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </TabsContent>

              {/* Settings Tab */}
              <TabsContent value="settings" className="space-y-4 mt-4" data-testid="tab-content-settings">
                <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <div>
                    <Label className="font-medium">Campaign Status</Label>
                    <p className="text-xs text-zinc-500">Enable to start sending messages</p>
                  </div>
                  <Switch
                    checked={formData.is_enabled}
                    onCheckedChange={(checked) => setFormData({ ...formData, is_enabled: checked })}
                    data-testid="switch-campaign-enabled"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Days Before Message 1</Label>
                    <Input
                      type="number"
                      min="1"
                      max="30"
                      value={formData.days_before_email_1}
                      onChange={(e) => setFormData({ ...formData, days_before_email_1: parseInt(e.target.value) || 1 })}
                      className="mt-1"
                      data-testid="input-days-before-msg1"
                    />
                    <p className="text-xs text-zinc-500 mt-1">Days in status before first message</p>
                  </div>
                  <div>
                    <Label>Days Between Messages</Label>
                    <Input
                      type="number"
                      min="1"
                      max="30"
                      value={formData.days_between_emails}
                      onChange={(e) => setFormData({ ...formData, days_between_emails: parseInt(e.target.value) || 1 })}
                      className="mt-1"
                      data-testid="input-days-between-msgs"
                    />
                    <p className="text-xs text-zinc-500 mt-1">Days after message 1 before message 2</p>
                  </div>
                </div>

                {/* Variables Info in Settings */}
                {editingCampaign?.variables && editingCampaign.variables.length > 0 && (
                  <div className="bg-slate-50 rounded-lg p-3">
                    <div className="text-xs font-medium text-zinc-700 mb-2">Available Variables for Templates:</div>
                    <div className="space-y-1">
                      {editingCampaign.variables.map((variable) => (
                        <div key={variable.name} className="flex items-center justify-between text-xs">
                          <code className="text-amber-700">{`{{${variable.name}}}`}</code>
                          <span className="text-zinc-500">{variable.description}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </TabsContent>
            </Tabs>

            <DialogFooter className="mt-4">
              <Button variant="outline" onClick={() => setEditingCampaign(null)} data-testid="btn-cancel">
                Cancel
              </Button>
              <Button onClick={handleSave} data-testid="btn-save-campaign">
                Save Changes
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </SuperAdminLayout>
  );
}
